import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, query, where, writeBatch } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { FaFilter, FaSyncAlt, FaTrashAlt, FaTimes } from "react-icons/fa";
import toast from "react-hot-toast";
import { db, auth } from "../Firebase";

const getDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [filters, setFilters] = useState({
    search: "",
    location: "all",
    propertyType: "all",
    dateFrom: "",
    dateTo: "",
    sort: "newest",
  });

  const fetchUsers = useCallback(async (adminData, showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      let usersQuery = collection(db, "users");
      if (adminData.role === "admin") {
        usersQuery = query(
          collection(db, "users"),
          where("location", "==", adminData.city),
          where("role", "==", "user")
        );
      }
      const snapshot = await getDocs(usersQuery);
      const list = snapshot.docs
        .map((userDoc) => ({ id: userDoc.id, ...userDoc.data() }))
        .filter((item) => adminData.role !== "superadmin" || item.role !== "superadmin");
      setUsers(list);
      setSelected(new Set());
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Could not load the user list.");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (!user) {
          setCurrentAdmin(null);
          return;
        }
        const adminSnapshot = await getDocs(
          query(collection(db, "admins"), where("email", "==", user.email))
        );
        if (adminSnapshot.empty) {
          setCurrentAdmin(null);
          return;
        }
        const adminData = adminSnapshot.docs[0].data();
        setCurrentAdmin(adminData);
        await fetchUsers(adminData);
      } catch (error) {
        console.error("Error fetching admin data:", error);
        toast.error("Could not verify this admin account.");
      } finally {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [fetchUsers]);

  const locations = useMemo(
    () => [...new Set(users.map((user) => user.location).filter(Boolean))].sort(),
    [users]
  );
  const propertyTypes = useMemo(
    () => [...new Set(users.map((user) => user.propertyType).filter(Boolean))].sort(),
    [users]
  );

  const filteredUsers = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    const from = filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`) : null;
    const to = filters.dateTo ? new Date(`${filters.dateTo}T23:59:59`) : null;
    return users
      .filter((user) => {
        const haystack = [user.name, user.mobile, user.email, user.location, user.propertyType]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        const created = getDate(user.createdAt);
        return (
          (!term || haystack.includes(term)) &&
          (filters.location === "all" || user.location === filters.location) &&
          (filters.propertyType === "all" || user.propertyType === filters.propertyType) &&
          (!from || (created && created >= from)) &&
          (!to || (created && created <= to))
        );
      })
      .sort((a, b) => {
        if (filters.sort === "name") return (a.name || "").localeCompare(b.name || "");
        const aDate = getDate(a.createdAt)?.getTime() || 0;
        const bDate = getDate(b.createdAt)?.getTime() || 0;
        return filters.sort === "oldest" ? aDate - bDate : bDate - aDate;
      });
  }, [filters, users]);

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: "", location: "all", propertyType: "all", dateFrom: "", dateTo: "", sort: "newest" });
    setSelected(new Set());
  };

  const toggleSelection = (id) => {
    setSelected((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allVisibleSelected = filteredUsers.length > 0 && filteredUsers.every((user) => selected.has(user.id));
  const toggleAllVisible = () => {
    setSelected((current) => {
      const next = new Set(current);
      if (allVisibleSelected) filteredUsers.forEach((user) => next.delete(user.id));
      else filteredUsers.forEach((user) => next.add(user.id));
      return next;
    });
  };

  const deleteUsers = async (ids) => {
    if (!ids.length) return;
    const label = ids.length === 1 ? "this user" : `${ids.length} selected users`;
    if (!window.confirm(`Permanently delete ${label}? This cannot be undone.`)) return;
    try {
      const chunks = [];
      for (let index = 0; index < ids.length; index += 450) chunks.push(ids.slice(index, index + 450));
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach((id) => batch.delete(doc(db, "users", id)));
        await batch.commit();
      }
      const idSet = new Set(ids);
      setUsers((current) => current.filter((user) => !idSet.has(user.id)));
      setSelected(new Set());
      toast.success(`${ids.length} user${ids.length === 1 ? "" : "s"} removed.`);
    } catch (error) {
      console.error("Error deleting users:", error);
      toast.error("The selected users could not be removed.");
    }
  };

  if (loading) return <div className="page-loader"><div className="spinner-border" role="status" /></div>;
  if (!currentAdmin) return <div className="empty-state text-danger">Please sign in with an authorised admin account.</div>;

  return (
    <div className="container-fluid admin-page user-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">People & enquiries</p>
          <h1>User management</h1>
          <p className="page-subtitle">Search, filter and clean your enquiry list.</p>
        </div>
        <button className="secondary-button" onClick={() => fetchUsers(currentAdmin, true)} disabled={refreshing}>
          <FaSyncAlt className={refreshing ? "spin" : ""} /> Refresh
        </button>
      </div>

      <section className="filter-panel" aria-label="User filters">
        <div className="filter-title"><FaFilter /> Filters</div>
        <div className="filter-grid">
          <div className="filter-field filter-search">
            <label htmlFor="user-search">Search</label>
            <input id="user-search" name="search" value={filters.search} onChange={updateFilter} placeholder="Name, mobile, email or city" />
          </div>
          <div className="filter-field">
            <label htmlFor="location-filter">Location</label>
            <select id="location-filter" name="location" value={filters.location} onChange={updateFilter}>
              <option value="all">All locations</option>
              {locations.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
          <div className="filter-field">
            <label htmlFor="property-filter">Property type</label>
            <select id="property-filter" name="propertyType" value={filters.propertyType} onChange={updateFilter}>
              <option value="all">All properties</option>
              {propertyTypes.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
          <div className="filter-field">
            <label htmlFor="from-filter">From</label>
            <input id="from-filter" type="date" name="dateFrom" value={filters.dateFrom} onChange={updateFilter} />
          </div>
          <div className="filter-field">
            <label htmlFor="to-filter">To</label>
            <input id="to-filter" type="date" name="dateTo" value={filters.dateTo} onChange={updateFilter} />
          </div>
          <div className="filter-field">
            <label htmlFor="sort-filter">Sort</label>
            <select id="sort-filter" name="sort" value={filters.sort} onChange={updateFilter}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="name">Name A–Z</option>
            </select>
          </div>
        </div>
        <button className="clear-filter-button" type="button" onClick={clearFilters}><FaTimes /> Clear filters</button>
      </section>

      <section className="data-card">
        <div className="data-toolbar">
          <div><strong>{filteredUsers.length}</strong> of {users.length} users</div>
          <button className="danger-button" disabled={!selected.size} onClick={() => deleteUsers([...selected])}>
            <FaTrashAlt /> Delete selected {selected.size ? `(${selected.size})` : ""}
          </button>
        </div>
        <div className="table-responsive">
          <table className="table admin-table align-middle mb-0">
            <thead><tr>
              <th className="select-cell"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} aria-label="Select all visible users" /></th>
              <th>Name</th><th>Contact</th><th>Property</th><th>Location</th><th>Date</th><th className="text-end">Action</th>
            </tr></thead>
            <tbody>
              {filteredUsers.map((user) => {
                const created = getDate(user.createdAt);
                return <tr key={user.id}>
                  <td data-label="Select" className="select-cell"><input type="checkbox" checked={selected.has(user.id)} onChange={() => toggleSelection(user.id)} aria-label={`Select ${user.name || "user"}`} /></td>
                  <td data-label="Name"><strong>{user.name || "Unnamed user"}</strong></td>
                  <td data-label="Contact"><div>{user.mobile || "—"}</div><small>{user.email || "No email"}</small></td>
                  <td data-label="Property"><span className="gold-badge">{user.propertyType || "Not specified"}</span></td>
                  <td data-label="Location">{user.location || "—"}</td>
                  <td data-label="Date">{created ? created.toLocaleDateString() : "—"}</td>
                  <td data-label="Action" className="text-end"><button className="icon-danger-button" onClick={() => deleteUsers([user.id])} aria-label={`Delete ${user.name || "user"}`}><FaTrashAlt /></button></td>
                </tr>;
              })}
              {!filteredUsers.length && <tr><td colSpan="7"><div className="empty-state">No users match the current filters.</div></td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
